// SAB-490 4c / SAB-493 / SAB-608: the branded block screen.
//
// CATEGORY-DRIVEN presenter (SAB-608). The certifier requires four block states that are
// never conflated:
//   jurisdiction  terminal, name the state, no retry, responsible-gaming line
//   technical     retriable, actionable (enable location / check connection / device)
//   vpn_proxy     retriable, name it, tell the user to disconnect
//   underage      terminal, no retry, responsible-gaming line
// Every state shows a support contact. Retry is offered ONLY on technical + vpn_proxy.
//
// Two producers feed this one presenter (they never mix):
//   - the GEO layer: useLocanceGeoCheck.geoBlock (a GeoBlockReason); its kinds collapse
//     to jurisdiction / technical / vpn_proxy via deriveCategory().
//   - the AGE gate (Persona terminal / check-eligibility verify_age): passes `underage`.
//     Underage is NOT a GeoBlockKind (it is not a location signal); it enters here as its
//     own prop and renders the underage category.
//
// Presentational only: it never calls the backend or the collector. The money gate stays
// server-authoritative; this screen only explains a block that already happened.

import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  MapPinOff,
  ClipboardCheck,
  ShieldAlert,
  Locate,
  AlertTriangle,
  Ban,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { GEO_FAQ_URL, type GeoBlockReason, type GeoBlockKind } from '@/hooks/useLocanceGeoCheck';
import { useFreePlayAvailable } from '@/lib/geoEligibility';

const SUPPORT_EMAIL = 'support@sabotagefantasy.com';

// The four standardized block categories. `profile_incomplete` is handled separately: it
// is a pre-verification nudge (fixable), not one of the four compliance blocks.
export type BlockCategory = 'jurisdiction' | 'technical' | 'vpn_proxy' | 'underage';

// Geo kinds collapse to categories. consent_denied / transient / unavailable / device are
// all verification failures (technical); each keeps its own actionable copy below.
function deriveCategory(kind: GeoBlockKind): BlockCategory {
  switch (kind) {
    case 'out_of_region': return 'jurisdiction';
    case 'vpn_proxy': return 'vpn_proxy';
    default: return 'technical';
  }
}

// Spec: retry is offered only on the recoverable categories (technical + vpn_proxy);
// jurisdiction and underage are terminal. This is enforced per-case in build() (those
// cases simply do not add a retry action).
//
// Spec: the responsible-gaming + age line shows only on jurisdiction + underage.
const SHOWS_RG_LINE: Record<BlockCategory, boolean> = {
  jurisdiction: true, technical: false, vpn_proxy: false, underage: true,
};

export interface GeoBlockScreenProps {
  /** The GEO block reason to render; null renders nothing (unless `underage`). */
  reason: GeoBlockReason | null;
  /**
   * AGE-gate producer: render the terminal UNDERAGE block. Underage is not a geo signal,
   * so it enters here rather than through GeoBlockKind. When true, `reason` is ignored.
   */
  underage?: boolean;
  /** True while a geo-check is in flight (disables retry actions). */
  busy?: boolean;
  /** Re-run the geo-check (typically geo.run()). Used by retry actions (technical/VPN). */
  onRetry?: () => void;
  /** Optional: back out of the money action (typically geo.reset()). */
  onDismiss?: () => void;
}

type ActionVariant = 'default' | 'outline' | 'link';
interface ActionSpec {
  label: string;
  variant: ActionVariant;
  onClick: () => void;
  disabled?: boolean;
}
interface ViewSpec {
  icon: LucideIcon;
  tone: 'damage' | 'primary';
  title: string;
  body: string;
  chip?: string | null;
  actions: ActionSpec[];
}

export function GeoBlockScreen({ reason, underage, busy, onRetry, onDismiss }: GeoBlockScreenProps) {
  const navigate = useNavigate();
  // Live-gate for the free-play CTA (jurisdiction). Hook must run before the early return.
  const freePlayAvailable = useFreePlayAvailable();
  if (!reason && !underage) return null;

  // profile_incomplete is a pre-verification nudge, kept separate from the four blocks.
  const isProfileIncomplete = !underage && reason?.kind === 'profile_incomplete';
  const category: BlockCategory = underage ? 'underage' : deriveCategory(reason!.kind);

  const stateLabel = reason?.detectedState ?? 'your state';
  const supportAction: ActionSpec = {
    label: 'Contact support',
    variant: 'outline',
    onClick: () => window.open(`mailto:${SUPPORT_EMAIL}`, '_blank', 'noopener,noreferrer'),
  };
  const faqAction: ActionSpec = {
    label: 'How location checks work',
    variant: 'link',
    onClick: () => window.open(GEO_FAQ_URL, '_blank', 'noopener,noreferrer'),
  };
  const retryAction = (label: string): ActionSpec => ({
    label: busy ? 'Checking…' : label,
    variant: 'default',
    onClick: () => onRetry?.(),
    disabled: busy,
  });
  const dismissAction: ActionSpec[] = onDismiss
    ? [{ label: 'Not now', variant: 'link', onClick: onDismiss, disabled: busy }]
    : [];

  const build = (): ViewSpec => {
    // AGE gate: terminal underage. No retry (governed by category); support in footer.
    if (underage) {
      return {
        icon: Ban,
        tone: 'damage',
        title: 'You must be 18 or older to play',
        body: `Real-money play requires you to be at least 18 (19 in Nebraska). Based on the date of birth we verified, you're not eligible to play for real money.`,
        actions: [supportAction],
      };
    }

    // Pre-verification nudge (not one of the four blocks): keep as-is.
    if (isProfileIncomplete) {
      return {
        icon: ClipboardCheck,
        tone: 'primary',
        title: 'Verify your state and age to play',
        body: 'Before you can play for real money, confirm the state you live in and your date of birth. It only takes a minute.',
        actions: [
          { label: 'Verify state & age', variant: 'default', onClick: () => navigate('/verify-eligibility') },
          ...dismissAction,
        ],
      };
    }

    switch (reason!.kind) {
      // ── jurisdiction (terminal, no retry) ──
      case 'out_of_region': {
        const declared = reason!.stateSource === 'declared';
        const actions: ActionSpec[] = [];
        if (freePlayAvailable) {
          actions.push({ label: 'Play free-to-play instead', variant: 'default', onClick: () => navigate('/nfl/leagues/browse') });
        }
        actions.push({
          label: 'See eligible states',
          variant: freePlayAvailable ? 'outline' : 'default',
          onClick: () => navigate('/state-availability'),
        });
        // No retry here: jurisdiction is terminal (spec). A misdetection is handled via
        // support, not a relocate-and-retry loop.
        return {
          icon: MapPinOff,
          tone: 'damage',
          title: 'Not available in your state',
          body: `Real-money contests aren't offered in ${stateLabel} yet. You can still join free-to-play leagues and keep your roster ready for when we launch.`,
          chip: reason!.detectedState
            ? declared
              ? `Registered state: ${reason!.detectedState}`
              : `Detected location: ${reason!.detectedState}, US`
            : null,
          actions,
        };
      }

      // ── technical: location permission off (retriable) ──
      case 'consent_denied': {
        const persistent = reason!.consentState === 'denied';
        return {
          icon: MapPinOff,
          tone: 'damage',
          title: 'Location access needed',
          body: persistent
            ? "Real-money play is only available where it's legal, so we need your location, but access is turned off. Enable location for this site in your device and browser settings, then try again."
            : "Real-money play is only available where it's legal, so we need your location. Turn on location access when prompted, then try again.",
          actions: [retryAction('Enable location & retry'), faqAction],
        };
      }

      // ── technical: transient location failure (retriable) ──
      case 'transient':
        return {
          icon: Locate,
          tone: 'damage',
          title: "Couldn't get your location",
          body: "We couldn't confirm where you are. Check your connection and make sure location is on, then try again.",
          actions: [retryAction('Try again'), faqAction],
        };

      // ── vpn_proxy (retriable, named) ──
      case 'vpn_proxy':
        return {
          icon: ShieldAlert,
          tone: 'damage',
          title: 'Turn off your VPN or proxy',
          body: "It looks like you're connecting through a VPN or proxy, so we couldn't verify your true location. Real-money play needs your actual location, so please turn it off and try again.",
          actions: [retryAction('Try again'), faqAction],
        };

      // ── technical: device verification (retriable, but NOT a location/connection issue,
      //    so distinct copy: telling the user to enable location here would be wrong) ──
      case 'device':
        return {
          icon: AlertTriangle,
          tone: 'damage',
          title: "Couldn't verify your device",
          body: "We couldn't verify this device for real-money play. Please try again. If it keeps happening, contact support.",
          actions: [retryAction('Try again'), supportAction],
        };

      // ── technical: everything else (retriable) ──
      case 'unavailable':
      default:
        return {
          icon: AlertTriangle,
          tone: 'damage',
          title: "Couldn't verify your location",
          body: 'Something went wrong verifying your location for real-money play. Please try again, or contact support if it keeps happening.',
          actions: [retryAction('Try again'), supportAction],
        };
    }
  };

  const v = build();
  const Icon = v.icon;
  const toneText = v.tone === 'damage' ? 'text-damage' : 'text-primary';
  const toneBg = v.tone === 'damage' ? 'bg-damage/15' : 'bg-primary/15';
  // Support contact shows on every block; the RG + age line only on jurisdiction/underage.
  const showRgLine = !isProfileIncomplete && SHOWS_RG_LINE[category];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={v.title}
      className={cn(
        'fixed inset-0 z-[200] flex items-center justify-center p-4',
        'bg-black/80 backdrop-blur-sm',
      )}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-border p-8 text-center shadow-lg"
        style={{ background: 'var(--gradient-card)' }}
      >
        <div
          className={cn(
            'mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full',
            toneBg,
          )}
        >
          <Icon className={cn('h-8 w-8', toneText)} />
        </div>

        <h2 className="text-2xl font-bold text-foreground">{v.title}</h2>
        <p className="mx-auto mt-3 max-w-sm text-sm text-muted-foreground">{v.body}</p>

        {v.chip && (
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span>{v.chip}</span>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-2">
          {v.actions.map((a, i) => (
            <Button
              key={i}
              onClick={a.onClick}
              disabled={a.disabled}
              variant={a.variant}
              className={cn('w-full', a.variant === 'link' && 'text-primary')}
            >
              {a.label}
            </Button>
          ))}
        </div>

        {/* Standardized footer. RG + age line: jurisdiction + underage only. Support: always. */}
        <div className="mx-auto mt-5 max-w-sm space-y-1.5 text-[11px] leading-relaxed text-muted-foreground/60">
          {showRgLine && (
            <p>
              Must be 18+ (19+ in Nebraska) and physically located in an eligible state to play paid
              contests. Responsible gaming: call or text 1-800-GAMBLER. Void where prohibited.
            </p>
          )}
          <p>
            Need help?{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary/80 hover:text-primary">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
